/*
  aycan-product-row — infinite coverflow carousel.
  Clones all items before and after real items to enable seamless looping.
  At scroll=0, first real item is centered; clone of last item is to its left.
*/
if (!customElements.get('aycan-product-row')) {
  customElements.define(
    'aycan-product-row',
    class AycanProductRow extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-prow-track]');
        this.prev = this.querySelector('[data-dir="prev"]');
        this.next = this.querySelector('[data-dir="next"]');
        if (!this.track) return;

        this._setupClones();

        this.prev && this.prev.addEventListener('click', () => this.go(-1));
        this.next && this.next.addEventListener('click', () => this.go(1));

        let raf, jumpTimer;
        this.track.addEventListener('scroll', () => {
          if (raf) return;
          raf = requestAnimationFrame(() => { raf = null; this.update(); });
          clearTimeout(jumpTimer);
          jumpTimer = setTimeout(() => this._jumpIfNeeded(), 220);
        }, { passive: true });
        window.addEventListener('resize', () => { this._initScroll(); this.update(); }, { passive: true });

        void this.track.offsetHeight;
        this._initScroll();
        this.update();
      }

      _setupClones() {
        const originals = Array.from(this.track.querySelectorAll('li'));
        this._n = originals.length;
        if (!this._n) return;

        // Pre-clones: same order as real → pre[n-1] is immediately left of real[0],
        // so scrolling left from real[0] reveals clone of real[n-1] first.
        const pre = document.createDocumentFragment();
        originals.forEach(el => {
          const c = el.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          pre.appendChild(c);
        });
        this.track.insertBefore(pre, this.track.firstChild);

        // Post-clones: same order
        originals.forEach(el => {
          const c = el.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          this.track.appendChild(c);
        });
      }

      _initScroll() {
        const n = this._n;
        if (!n) return;
        const el = this._allItems()[n]; // first real item
        if (!el) return;
        this.track.scrollLeft = el.offsetLeft + el.offsetWidth / 2 - this.track.clientWidth / 2;
      }

      _allItems() { return Array.from(this.track.querySelectorAll('li')); }

      nearest() {
        const cx = this.track.scrollLeft + this.track.clientWidth / 2;
        let best = 0, min = Infinity;
        this._allItems().forEach((el, i) => {
          const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - cx);
          if (d < min) { min = d; best = i; }
        });
        return best;
      }

      // Real index 0..n-1 for any global index (works for pre/post clones too)
      _realIdx(i) {
        const n = this._n;
        return ((i - n) % n + n) % n;
      }

      update() {
        const active = this._realIdx(this.nearest());
        this._allItems().forEach((el, i) => el.classList.toggle('is-active', this._realIdx(i) === active));
      }

      centerIndex(i) {
        const el = this._allItems()[i];
        if (!el) return;
        this.track.scrollTo({ left: el.offsetLeft + el.offsetWidth / 2 - this.track.clientWidth / 2, behavior: 'smooth' });
      }

      go(dir) {
        // Navigate into clone zone so the smooth scroll shows the loop visually.
        this.centerIndex(this.nearest() + dir);
      }

      _jumpIfNeeded() {
        const n = this._n;
        const idx = this.nearest();
        let jumpTo = null;
        if (idx < n)       jumpTo = n + idx;         // pre-clone → real equivalent
        else if (idx >= 2 * n) jumpTo = n + (idx - 2 * n); // post-clone → real equivalent
        if (jumpTo === null) return;
        const el = this._allItems()[jumpTo];
        if (!el) return;
        this.track.scrollLeft = el.offsetLeft + el.offsetWidth / 2 - this.track.clientWidth / 2;
        this.update();
      }
    }
  );
}
